import { HeartHandshake, Eye } from 'lucide-react';
import { Eyebrow, Reveal } from './Reveal';

const VERTICALS = [
  {
    testid: 'ecosystem-card-talent',
    name: 'Sania Talent Solutions',
    badge: 'Core service · Official logo',
    logo: '/assets/sania-logo-blue.png',
    logoClass: 'max-h-14 w-auto rounded-xl shadow-sm',
    subtitle: 'Recruitment & Talent Acquisition',
    desc: 'Permanent recruitment, candidate sourcing, screening and hiring support for employers.',
  },
  {
    testid: 'ecosystem-card-oneday',
    name: 'Sania One Day Hiring',
    badge: null,
    logo: '/assets/one-day-logo.png',
    logoClass: 'max-h-14 w-auto rounded-xl shadow-sm',
    desc: 'Flexible daily, weekend, event and project-based workforce deployment.',
  },
  {
    testid: 'ecosystem-card-payroll',
    name: 'Sania Payroll Solutions',
    badge: null,
    logo: '/assets/payroll-logo.png',
    logoClass: 'max-h-14 w-auto rounded-xl shadow-sm',
    desc: 'Payroll, employee records, attendance and workforce administration support.',
  },
];

const FUTURE_VERTICALS = [
  {
    testid: 'future-card-jobmitra',
    name: 'Sania Job Mitra',
    desc: 'Local employment assistance centres connecting communities with opportunities.',
  },
  {
    testid: 'future-card-skill',
    name: 'Sania Skill Academy',
    desc: 'Training-partner connections and industry-relevant employability pathways.',
  },
  {
    testid: 'future-card-educational',
    name: 'Sania Educational Services',
    desc: 'Education counselling, admissions guidance and academic pathways.',
  },
  {
    testid: 'future-card-global',
    name: 'Sania Global Placements',
    desc: 'Developing overseas career pathways, including Gulf markets, subject to applicable regulations.',
  },
];

export default function Ecosystem() {
  return (
    <section id="about" data-testid="about-section" className="scroll-mt-28 py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-36">
              <Reveal>
                <Eyebrow index="02">About Us</Eyebrow>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-[2.75rem] lg:leading-[1.12]">
                  About Sania Job Bowl
                </h2>
                <p className="mt-3 font-display text-lg font-bold text-royal sm:text-xl">
                  One ecosystem. Seven specialized verticals.
                </p>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mt-8 space-y-4">
                  <div className="flex gap-4 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-card transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-lift">
                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-lime-soft text-lime-deep">
                      <HeartHandshake className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div>
                      <h3 className="font-display text-base font-extrabold text-ink">Our mission</h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">
                        Make hiring simpler, more accessible and more dependable.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-card transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-lift">
                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-pine-soft text-pine">
                      <Eye className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div>
                      <h3 className="font-display text-base font-extrabold text-ink">Our vision</h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">
                        Build a trusted employment ecosystem.
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-base leading-relaxed text-slate-600 sm:text-lg">
                Sania Job Bowl Pvt. Ltd. is an Indian workforce and career solutions company building connections
                between businesses, talent, education and employment opportunities.
              </p>
              <p className="mt-5 text-base leading-relaxed text-slate-600">
                Our work starts with a simple belief: businesses should be able to find and manage the right workforce
                without unnecessary complexity, and people should have better access to employment. Our seven
                specialized verticals span the employment journey: identifying the right candidates, deploying
                flexible teams, managing payroll, improving access to local jobs, supporting skill development,
                guiding students and developing international placement pathways. Employers can start with one service
                and explore additional support as their needs evolve.
              </p>
              <p className="mt-5 text-base leading-relaxed text-slate-600">
                For employers, we begin with the realities of the operation: required roles, headcount, location,
                working hours, joining timelines and budget. We then coordinate the appropriate recruitment,
                deployment or payroll solution. For candidates, our goal is to make access to suitable work more
                straightforward and human. We value transparent communication, documented expectations and lasting
                partnerships. Our teams coordinate practical details such as interview schedules, staff reporting
                times, location instructions, joining documents and attendance, so businesses can spend more time on
                their customers and operations.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="relative mt-10 overflow-hidden rounded-[2rem] shadow-lift">
                <img
                  src="https://images.unsplash.com/photo-1781246081182-b8e658662ebd?q=80&w=1600&auto=format&fit=crop"
                  alt="Indian team collaborating at Sania Job Bowl"
                  loading="lazy"
                  className="aspect-[16/9] w-full object-cover"
                />
                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-royal-ink/40 via-transparent to-transparent" />
              </div>
            </Reveal>
          </div>
        </div>

        {/* Ecosystem grid */}
        <div id="ecosystem" className="scroll-mt-32 pt-20 lg:pt-28">
          <Reveal>
            <Eyebrow index="03">Our ecosystem</Eyebrow>
            <h3 className="mt-5 font-display text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
              Seven verticals. One connected purpose.
            </h3>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
              Our three core commercial services are supported by a wider ecosystem of employment, skill-development
              and career initiatives.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="ecosystem-grid">
            {VERTICALS.map((v, i) => (
              <Reveal key={v.testid} delay={(i % 3) * 0.08} className="h-full">
                <article
                  data-testid={v.testid}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200/80 bg-white p-6 shadow-card transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-royal/30 hover:shadow-lift"
                >
                  <div className="flex h-16 items-center">
                    <img src={v.logo} alt={`Official ${v.name} logo`} loading="lazy" className={v.logoClass} />
                  </div>
                  <h4 className="mt-4 font-display text-base font-extrabold leading-snug text-ink">{v.name}</h4>
                  {v.badge && (
                    <p className="mt-1.5 inline-flex w-fit rounded-full bg-porcelain px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                      {v.badge}
                    </p>
                  )}
                  {v.subtitle && <p className="mt-2 text-sm font-bold text-royal">{v.subtitle}</p>}
                  <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600">{v.desc || v.subtitle}</p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <p
              className="mt-16 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400"
              data-testid="ecosystem-future-label"
            >
              Coming in the future
            </p>
          </Reveal>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="ecosystem-future-grid">
            {FUTURE_VERTICALS.map((v, i) => (
              <Reveal key={v.testid} delay={(i % 4) * 0.08} className="h-full">
                <article
                  data-testid={v.testid}
                  className="flex h-full flex-col rounded-2xl border border-dashed border-slate-300 bg-porcelain/70 p-6 transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1 hover:border-royal/30 hover:bg-white"
                >
                  <span className="inline-flex w-fit rounded-full border border-slate-300 bg-white px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                    Coming soon
                  </span>
                  <h4 className="mt-4 font-display text-base font-extrabold leading-snug text-ink">{v.name}</h4>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-slate-500">{v.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
